import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import ClientSelector from './components/ClientSelector';
import EmployeeSelector from './components/EmployeeSelector';
import PieceDetailsForm from './components/PieceDetailsForm';
import RendimientoSection from './components/RendimientoSection';
import FotografiaSection from './components/FotografiaSection';
import RendimientoAnalytics from './components/RendimientoAnalytics';
import {
  ERPPage, ERPPageHeader, ERPKPIGrid, ERPKPICard, ERPCard,
  ERPButton, ERPError, ERPLoading
} from '../../components/ui/ERPLayout';
import { supabase } from '../../lib/supabase';
import { calcularMerma, calcularRendimiento, rendimientoService } from '../../services/rendimientoService';
import { productService } from '../../services/productService';
import { calcularTarifa, calcularCosteSala, getTarifaDescripcion } from '../../utils/tarifaCalculation';
import { planTrabajoPiezasService } from '../../services/planTrabajoService';
import PlanTrabajoSelector from './components/PlanTrabajoSelector';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const DEFAULT_LOTE_INTERNO = 'LDD-3 / 690';

const formatDateYYYYMMDD = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const yyyy = d?.getFullYear();
  const mm = String(d?.getMonth() + 1)?.padStart(2, '0');
  const dd = String(d?.getDate())?.padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
};

const inputCls = 'w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2.5 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors';
const labelCls = 'block font-oranienbaum mb-1.5 text-white text-[17px]';

// ─── Mode detection ───────────────────────────────────────────────────────────
// MODO CREAR:    ?trabajo=<UUID>&pieza=<UUID>   → no loncheado_id yet
// MODO VER/EDITAR: ?id=<loncheado_id>           → load existing loncheado

const PieceRegistration = () => {
  const [searchParams] = useSearchParams();

  // Mode params
  const editId = searchParams?.get('id') || null;           // VIEW/EDIT mode
  const planTrabajoId = searchParams?.get('trabajo') || null; // CREATE mode
  const planPiezaId = searchParams?.get('pieza') || null;    // CREATE mode

  // Derived mode flags
  const isViewEdit = !!editId;
  const isFromPlan = !!(planTrabajoId && planPiezaId) && !isViewEdit;

  // ── State ─────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState(null); // 'create' | 'edit' | 'standalone'
  const [planContext, setPlanContext] = useState(null); // loaded from Supabase
  const [btnLoading, setBtnLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [savedLoncheadoId, setSavedLoncheadoId] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [generatingLote, setGeneratingLote] = useState(false);
  const [kpis, setKpis] = useState({ piezasRegistradas: 0, rendimientoMedio: 0, kgProcesados: 0, clientesActivos: 0 });
  const [kpisLoading, setKpisLoading] = useState(true);
  const submittingRef = useRef(false); // double-submit guard

  const handleSelectorContextLoaded = useCallback((ctx) => {
    if (!ctx) return;
    setPlanContext(ctx);
    setFormData(prev => ({
      ...prev,
      clientId: ctx.clienteId || ctx.clientId || prev.clientId,
      employeeId: ctx.empleadoId || ctx.employeeId || prev.employeeId,
      productId: ctx.productoId || ctx.productId || prev.productId,
      cutType: ctx.tipoCorte || ctx.cutType || prev.cutType,
      observaciones: ctx.instrucciones || ctx.observaciones || prev.observaciones,
    }));
  }, []);

  const handleSelectorClear = useCallback(() => {
    setPlanContext(null);
    setFormData(prev => ({
      ...prev,
      clientId: null,
      employeeId: null,
      productId: null,
      cutType: 'NORMAL',
      observaciones: '',
    }));
  }, []);

  const [formData, setFormData] = useState({
    clientId: null,
    employeeId: null,
    productId: null,
    weight: '',
    cutType: 'NORMAL',
    registrationDate: new Date()?.toISOString()?.split('T')?.[0],
    registrationTime: new Date()?.toTimeString()?.slice(0, 5),
    observaciones: '',
    pesoAprovechado: '',
    fotos: [],
    loteInterno: DEFAULT_LOTE_INTERNO,
    loteExterno: '',
    numeroBlisteres: 0,
  });

  const [errors, setErrors] = useState({});
  const [clientes, setClientes] = useState([]);

  // ── Init: determine mode and load data ────────────────────────────────────
  useEffect(() => {
    initPage();
  }, []);

  const initPage = async () => {
    setPageLoading(true);
    try {
      await Promise.all([loadKPIs(), loadClientes()]);

      if (isViewEdit) {
        // MODO VER/EDITAR — load existing loncheado by id
        setMode('edit');
        await loadExistingRecord(editId);
      } else if (isFromPlan) {
        // MODO CREAR desde Plan de Trabajo — load from Supabase by IDs
        setMode('create');
        await loadFromPlan(planTrabajoId, planPiezaId);
      } else {
        // MODO STANDALONE — empty form
        setMode('standalone');
        await setDefaultProduct();
        await generateLoteExterno(new Date()?.toISOString()?.split('T')?.[0]);
      }
    } catch (err) {
      console.error('[PieceRegistration.initPage]', err);
      setSubmitError('Error al inicializar el formulario: ' + (err?.message || ''));
    } finally {
      setPageLoading(false);
    }
  };

  // ── Load plan context from Supabase (MODO CREAR) ──────────────────────────
  const loadFromPlan = async (trabajoId, piezaId) => {
    try {
      // 1. Load the pieza with its trabajo
      const { data: pieza, error: piezaErr } = await supabase
        .from('plan_trabajo_piezas')
        .select('*, producto:productos(id, nombre), empleado:empleados(id, nombre)')
        .eq('id', piezaId)
        .single();

      if (piezaErr) throw new Error('No se encontró la pieza: ' + piezaErr.message);

      // 2. Validate pieza belongs to the trabajo
      if (pieza.trabajo_id !== trabajoId) {
        throw new Error('La pieza no pertenece al trabajo indicado. Combinación inválida.');
      }

      // 3. If pieza already has loncheado_id → redirect to VIEW mode
      if (pieza.loncheado_id) {
        console.log('[PieceRegistration] Pieza ya tiene loncheado, redirigiendo a VER REGISTRO');
        window.location.href = `/piece-registration?id=${pieza.loncheado_id}`;
        return;
      }

      // 4. Check for existing loncheado for this pieza (duplicate prevention)
      const { data: existingLoncheado } = await supabase
        .from('loncheados')
        .select('id')
        .eq('plan_trabajo_pieza_id', piezaId)
        .maybeSingle();

      if (existingLoncheado?.id) {
        console.log('[PieceRegistration] Loncheado duplicado detectado, redirigiendo a VER REGISTRO');
        // Link it if not already linked
        try {
          await planTrabajoPiezasService?.linkLoncheado(piezaId, existingLoncheado.id);
        } catch {}
        window.location.href = `/piece-registration?id=${existingLoncheado.id}`;
        return;
      }

      // 5. Load the trabajo padre
      const { data: trabajo, error: trabajoErr } = await supabase
        .from('plan_trabajo')
        .select('*, cliente:clientes(id, nombre), producto:productos(id, nombre), empleado:empleados(id, nombre)')
        .eq('id', trabajoId)
        .single();

      if (trabajoErr) throw new Error('No se encontró el trabajo: ' + trabajoErr.message);

      // 6. Resolve fields: pieza-level takes priority, fallback to trabajo-level
      const resolvedClientId = trabajo?.cliente_id || null;
      const resolvedProductId = pieza?.producto_id || trabajo?.producto_id || null;
      const resolvedEmployeeId = pieza?.empleado_id || trabajo?.empleado_id || null;
      const resolvedTipoCorte = pieza?.tipo_corte || trabajo?.tipo_corte || 'NORMAL';
      const resolvedFormatoCorte = pieza?.formato_corte || trabajo?.formato_corte || '';
      const resolvedQuiereHuesos = pieza?.quiere_huesos != null ? pieza.quiere_huesos : (trabajo?.quiere_huesos || false);
      const resolvedGuardarMerma = pieza?.guardar_merma != null ? pieza.guardar_merma : (trabajo?.guardar_merma || false);
      const resolvedObservaciones = pieza?.observaciones_operario || trabajo?.instrucciones || trabajo?.observaciones || '';

      // 7. Store plan context for display and locking
      const ctx = {
        pieza,
        trabajo,
        clienteNombre: trabajo?.cliente?.nombre || '',
        productoNombre: pieza?.producto?.nombre || trabajo?.producto?.nombre || '',
        empleadoNombre: pieza?.empleado?.nombre || trabajo?.empleado?.nombre || '',
        formatoCorte: resolvedFormatoCorte,
        quiereHuesos: resolvedQuiereHuesos,
        guardarMerma: resolvedGuardarMerma,
        observacionesPlan: resolvedObservaciones,
        numeroPieza: pieza?.numero_pieza,
      };
      setPlanContext(ctx);

      // 8. Preload form
      setFormData(prev => ({
        ...prev,
        clientId: resolvedClientId,
        employeeId: resolvedEmployeeId,
        productId: resolvedProductId,
        cutType: resolvedTipoCorte,
        observaciones: resolvedObservaciones,
      }));

      await generateLoteExterno(new Date()?.toISOString()?.split('T')?.[0]);
    } catch (err) {
      console.error('[PieceRegistration.loadFromPlan]', err);
      setSubmitError(err.message);
    }
  };

  const loadClientes = async () => {
    try {
      const { data } = await supabase.from('clientes').select('id, nombre').eq('activo', true);
      setClientes(data || []);
    } catch { /* silent */ }
  };

  const clienteSeleccionado = clientes?.find(c => c?.id === formData?.clientId);
  const isLasDosCliente = clienteSeleccionado?.nombre?.toLowerCase()?.includes('dos doncellas') || false;

  const generateLoteExterno = async (dateStr) => {
    try {
      setGeneratingLote(true);
      const formatted = formatDateYYYYMMDD(new Date(dateStr));
      const { data, error: rpcError } = await supabase?.rpc('get_next_lote_externo', { p_date: formatted });
      if (rpcError) throw rpcError;
      if (data) {
        setFormData(prev => ({ ...prev, loteExterno: data }));
      }
    } catch (err) {
      console.warn('Error generating lote externo:', err?.message);
      const formatted = formatDateYYYYMMDD(new Date(dateStr));
      setFormData(prev => ({ ...prev, loteExterno: `LDD-${formatted}-001` }));
    } finally {
      setGeneratingLote(false);
    }
  };

  const setDefaultProduct = async () => {
    try {
      const data = await productService?.getAll();
      const products = (data || [])?.filter(p => p?.activo);
      const defaultProduct =
        products?.find(p => p?.nombre?.toLowerCase()?.includes('cebo 50')) ||
        products?.find(p => p?.nombre?.toLowerCase()?.includes('jamón ibérico cebo')) ||
        products?.find(p => p?.nombre?.toLowerCase()?.includes('jamon iberico cebo')) ||
        products?.find(p => p?.nombre?.toLowerCase()?.includes('ibérico cebo')) ||
        products?.[0];

      if (defaultProduct) {
        setFormData(prev => ({ ...prev, productId: defaultProduct?.id, cutType: 'NORMAL' }));
      }
    } catch { /* silent */ }
  };

  const loadKPIs = async () => {
    setKpisLoading(true);
    try {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

      const { data: loncheados } = await supabase
        .from('loncheados')
        .select('peso_inicial, peso, rendimiento, peso_aprovechado, cliente_id')
        .gte('created_at', firstDay)
        .lte('created_at', lastDay + 'T23:59:59');

      const { data: clientesData } = await supabase.from('clientes').select('id').eq('activo', true);

      const piezas = loncheados?.length || 0;

      // Usar peso_inicial como fuente principal de kg procesados (NO peso_loncheado)
      const kgTotal = loncheados?.reduce((s, l) => {
        const pi = parseFloat(l?.peso_inicial);
        return s + (isNaN(pi) || pi <= 0 ? 0 : pi);
      }, 0);

      // Rendimiento medio: solo registros con datos válidos y consistentes
      // Normalizar: si rendimiento está entre 0-1 → multiplicar ×100; si 1-100 → usar directo
      const rendimientosValidos = (loncheados || []).reduce((acc, l) => {
        const pi = parseFloat(l?.peso_inicial);
        const pa = parseFloat(l?.peso_aprovechado);
        // Calcular rendimiento desde pesos si están disponibles
        if (!isNaN(pi) && pi > 0 && !isNaN(pa) && pa >= 0 && pa <= pi) {
          acc.push((pa / pi) * 100);
          return acc;
        }
        // Fallback: usar campo rendimiento almacenado si existe
        const r = parseFloat(l?.rendimiento);
        if (!isNaN(r) && r > 0) {
          const rNorm = r <= 1 ? r * 100 : r;
          if (rNorm >= 0 && rNorm <= 100) acc.push(rNorm);
        }
        return acc;
      }, []);

      const rendMedio = rendimientosValidos.length > 0
        ? rendimientosValidos.reduce((a, b) => a + b, 0) / rendimientosValidos.length
        : 0;

      setKpis({
        piezasRegistradas: piezas,
        rendimientoMedio: rendMedio,
        kgProcesados: kgTotal,
        clientesActivos: clientesData?.length || 0
      });
    } catch { /* silent */ } finally {
      setKpisLoading(false);
    }
  };

  // ── Load existing loncheado (MODO VER/EDITAR) ─────────────────────────────
  const loadExistingRecord = async (id) => {
    setLoadingEdit(true);
    try {
      const { data, error } = await supabase?.from('loncheados')?.select('*')?.eq('id', id)?.single();
      if (error) throw error;
      if (!data) return;

      let existingFotos = [];
      if (data?.foto_url) {
        try {
          const parsed = JSON.parse(data.foto_url);
          existingFotos = Array.isArray(parsed)
            ? parsed.map((url, i) => ({ url, name: `Foto ${i + 1}`, remote: true }))
            : [{ url: data.foto_url, name: 'Foto 1', remote: true }];
        } catch {
          existingFotos = [{ url: data.foto_url, name: 'Foto 1', remote: true }];
        }
      }

      const createdAt = data?.created_at ? new Date(data.created_at) : new Date();

      setFormData({
        clientId: data?.cliente_id || null,
        employeeId: data?.empleado_id || null,
        productId: data?.producto_id || null,
        weight: data?.peso != null ? String(data.peso) : data?.peso_loncheado != null ? String(data.peso_loncheado) : '',
        cutType: data?.tipo || 'NORMAL',
        registrationDate: createdAt?.toISOString()?.split('T')?.[0],
        registrationTime: createdAt?.toTimeString()?.slice(0, 5),
        observaciones: data?.observaciones || '',
        pesoAprovechado: data?.peso_aprovechado != null ? String(data.peso_aprovechado) : '',
        fotos: existingFotos,
        loteInterno: data?.lote_interno || DEFAULT_LOTE_INTERNO,
        loteExterno: data?.lote_externo || '',
        numeroBlisteres: data?.numero_blisteres != null ? data.numero_blisteres : 0,
      });
      setSavedLoncheadoId(id);

      if (!data?.lote_externo) {
        generateLoteExterno(createdAt?.toISOString()?.split('T')?.[0]);
      }

      // Load plan context if this loncheado is linked to a pieza
      if (data?.plan_trabajo_pieza_id) {
        try {
          const { data: pieza } = await supabase
            .from('plan_trabajo_piezas')
            .select('*, producto:productos(id, nombre), empleado:empleados(id, nombre)')
            .eq('id', data.plan_trabajo_pieza_id)
            .single();
          if (pieza) {
            const { data: trabajo } = await supabase
              .from('plan_trabajo')
              .select('*, cliente:clientes(id, nombre), producto:productos(id, nombre), empleado:empleados(id, nombre)')
              .eq('id', pieza.trabajo_id)
              .single();
            if (trabajo) {
              setPlanContext({
                pieza,
                trabajo,
                clienteNombre: trabajo?.cliente?.nombre || '',
                productoNombre: pieza?.producto?.nombre || trabajo?.producto?.nombre || '',
                empleadoNombre: pieza?.empleado?.nombre || trabajo?.empleado?.nombre || '',
                formatoCorte: pieza?.formato_corte || trabajo?.formato_corte || '',
                quiereHuesos: pieza?.quiere_huesos ?? trabajo?.quiere_huesos ?? false,
                guardarMerma: pieza?.guardar_merma ?? trabajo?.guardar_merma ?? false,
                observacionesPlan: pieza?.observaciones_operario || trabajo?.instrucciones || trabajo?.observaciones || '',
                numeroPieza: pieza?.numero_pieza,
              });
            }
          }
        } catch { /* silent — plan context is optional for view mode */ }
      }
    } catch (err) {
      setSubmitError('Error al cargar el registro: ' + (err?.message || ''));
    } finally {
      setLoadingEdit(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.clientId) newErrors.clientId = 'Seleccione un cliente';
    if (!formData?.employeeId) newErrors.employeeId = 'Seleccione un empleado';
    if (!formData?.weight || parseFloat(formData?.weight) <= 0) newErrors.weight = 'Peso válido requerido';
    else if (parseFloat(formData?.weight) > 50) newErrors.weight = 'Máximo 50 kg';
    if (!formData?.cutType) newErrors.cutType = 'Seleccione tipo de gestión';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    // ── Double-submit guard ──────────────────────────────────────────────────
    if (submittingRef.current || btnLoading) return;
    submittingRef.current = true;
    setBtnLoading(true);
    setSubmitError(null);

    try {
      const pesoValue = parseFloat(formData?.weight);
      const pesoAprovechadoValue = formData?.pesoAprovechado ? parseFloat(formData.pesoAprovechado) : null;
      const mermaValue = calcularMerma(formData?.weight, formData?.pesoAprovechado);
      const rendimientoValue = calcularRendimiento(formData?.weight, formData?.pesoAprovechado);
      const tarifa = calcularTarifa(formData?.cutType, pesoValue);
      const costeSala = calcularCosteSala(formData?.cutType, pesoValue);

      const remoteFotos = formData?.fotos?.filter((f) => f?.remote);
      const localFotos = formData?.fotos?.filter((f) => !f?.remote && f?.file);
      let allRemoteUrls = remoteFotos?.map((f) => f?.url) || [];

      const loncheadoPayload = {
        cliente_id: formData?.clientId,
        empleado_id: formData?.employeeId,
        producto_id: formData?.productId || null,
        peso: pesoValue,
        peso_loncheado: pesoValue,
        peso_inicial: pesoValue,
        peso_aprovechado: pesoAprovechadoValue,
        merma: mermaValue,
        rendimiento: rendimientoValue ? parseFloat(rendimientoValue?.toFixed(2)) : null,
        tipo: formData?.cutType,
        observaciones: formData?.observaciones || null,
        tarifa: tarifa,
        coste_sala: costeSala,
        lote_interno: formData?.loteInterno || DEFAULT_LOTE_INTERNO,
        lote_externo: formData?.loteExterno || null,
        numero_blisteres: isLasDosCliente
          ? (parseInt(formData?.numeroBlisteres) || 0)
          : (formData?.numeroBlisteres != null ? parseInt(formData?.numeroBlisteres) : null),
        // ── formato_corte: save from plan context or form selection ──
        formato_corte: planContext?.formatoCorte || formData?.formatoCorte || null,
        // ── Plan de Trabajo links ──
        ...(planTrabajoId ? { plan_trabajo_id: planTrabajoId } : {}),
        ...(planPiezaId ? { plan_trabajo_pieza_id: planPiezaId } : {}),
      };

      let recordId = savedLoncheadoId;

      if (isViewEdit && recordId) {
        // ── MODO VER/EDITAR: UPDATE existing loncheado ──────────────────────
        const { error: updateError } = await supabase
          ?.from('loncheados')
          ?.update(loncheadoPayload)
          ?.eq('id', recordId)
          ?.select()
          ?.single();
        if (updateError) throw updateError;
      } else {
        // ── MODO CREAR: check for existing loncheado first (duplicate guard) ──
        if (planPiezaId) {
          const { data: existing } = await supabase
            .from('loncheados')
            .select('id')
            .eq('plan_trabajo_pieza_id', planPiezaId)
            .maybeSingle();

          if (existing?.id) {
            // Already exists — update instead of inserting a duplicate
            console.warn('[PieceRegistration] Loncheado ya existe para esta pieza, actualizando en lugar de insertar');
            const { error: updateError } = await supabase
              ?.from('loncheados')
              ?.update(loncheadoPayload)
              ?.eq('id', existing.id)
              ?.select()
              ?.single();
            if (updateError) throw updateError;
            recordId = existing.id;
            setSavedLoncheadoId(recordId);

            // Ensure pieza is linked
            try {
              await planTrabajoPiezasService?.linkLoncheado(planPiezaId, recordId);
            } catch (piezaErr) {
              console.warn('No se pudo vincular pieza al loncheado:', piezaErr?.message);
            }
          } else {
            // Insert new loncheado
            const { data: inserted, error: insertError } = await supabase
              ?.from('loncheados')
              ?.insert([loncheadoPayload])
              ?.select()
              ?.single();
            if (insertError) throw insertError;
            recordId = inserted?.id;
            setSavedLoncheadoId(recordId);

            // Auto-create salary record
            try {
              await supabase?.from('salarios')?.insert([{
                empleado_id: formData?.employeeId,
                loncheado_id: recordId,
                salario: tarifa,
                coste_empresa: tarifa,
                fecha: formData?.registrationDate || new Date()?.toISOString()?.split('T')?.[0],
                tipo: formData?.cutType
              }]);
            } catch (salErr) {
              console.warn('No se pudo crear registro de salario automáticamente:', salErr?.message);
            }

            // Link pieza to loncheado and mark COMPLETADA
            try {
              await planTrabajoPiezasService?.linkLoncheado(planPiezaId, recordId);
            } catch (piezaErr) {
              console.warn('No se pudo vincular pieza al loncheado:', piezaErr?.message);
            }
          }
        } else {
          // Standalone mode — just insert
          const { data: inserted, error: insertError } = await supabase
            ?.from('loncheados')
            ?.insert([loncheadoPayload])
            ?.select()
            ?.single();
          if (insertError) throw insertError;
          recordId = inserted?.id;
          setSavedLoncheadoId(recordId);

          // Auto-create salary record
          try {
            await supabase?.from('salarios')?.insert([{
              empleado_id: formData?.employeeId,
              loncheado_id: recordId,
              salario: tarifa,
              coste_empresa: tarifa,
              fecha: formData?.registrationDate || new Date()?.toISOString()?.split('T')?.[0],
              tipo: formData?.cutType
            }]);
          } catch (salErr) {
            console.warn('No se pudo crear registro de salario automáticamente:', salErr?.message);
          }
        }
      }

      // Upload photos
      if (recordId && localFotos?.length > 0) {
        for (const foto of localFotos) {
          try {
            const { publicUrl } = await rendimientoService?.uploadFoto(recordId, foto?.file);
            allRemoteUrls?.push(publicUrl);
          } catch {}
        }
      }

      if (recordId && allRemoteUrls?.length > 0) {
        const fotoUrlToSave = allRemoteUrls?.length === 1 ? allRemoteUrls[0] : JSON.stringify(allRemoteUrls);
        await supabase?.from('loncheados')?.update({ foto_url: fotoUrlToSave })?.eq('id', recordId);
      }

      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 4000);

      // Navigate back to plan after save
      if (isFromPlan || (isViewEdit && planContext?.pieza)) {
        setTimeout(() => {
          window.location.href = '/plan-trabajo';
        }, 2000);
      } else {
        handleReset();
      }
      loadKPIs();
    } catch (error) {
      setSubmitError(error?.message || 'Error al guardar el registro.');
    } finally {
      setBtnLoading(false);
      submittingRef.current = false;
    }
  };

  const handleReset = () => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    setFormData({
      clientId: null, employeeId: null, productId: null, weight: '', cutType: 'NORMAL',
      registrationDate: today,
      registrationTime: new Date()?.toTimeString()?.slice(0, 5),
      observaciones: '', pesoAprovechado: '', fotos: [],
      loteInterno: DEFAULT_LOTE_INTERNO,
      loteExterno: '',
      numeroBlisteres: 0,
    });
    setErrors({});
    setSavedLoncheadoId(null);
    setDefaultProduct();
    generateLoteExterno(today);
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const mermaRealTime = calcularMerma(formData?.weight, formData?.pesoAprovechado);
  const rendimientoRealTime = calcularRendimiento(formData?.weight, formData?.pesoAprovechado);
  const tarifaPreview = formData?.weight && parseFloat(formData?.weight) > 0 && formData?.cutType
    ? calcularTarifa(formData?.cutType, formData?.weight)
    : null;
  const costeSalaPreview = tarifaPreview;
  const tarifaDescripcion = formData?.weight && parseFloat(formData?.weight) > 0 && formData?.cutType
    ? getTarifaDescripcion(formData?.cutType, formData?.weight)
    : null;

  const isFromPlanOrEdit = isFromPlan || (isViewEdit && !!planContext?.pieza);
  const isFieldLocked = isFromPlan; // Only lock fields in CREATE mode from plan

  const kpiCards = [
    { title: 'Piezas Registradas', value: kpis.piezasRegistradas, subtitle: 'Este mes', icon: 'Package', iconColor: '#D6EF4E', valueColor: '#D6EF4E' },
    { title: 'Rendimiento Medio', value: `${kpis.rendimientoMedio.toFixed(1)}%`, subtitle: 'Promedio mensual', icon: 'TrendingUp', iconColor: '#3A7F6B', valueColor: '#3A7F6B' },
    { title: 'Kg Procesados', value: `${kpis.kgProcesados.toFixed(1)} kg`, subtitle: 'Total del mes', icon: 'Scale', iconColor: '#C9A24D', valueColor: '#C9A24D' },
    { title: 'Clientes Activos', value: kpis.clientesActivos, subtitle: 'En el sistema', icon: 'Users', iconColor: '#8097D6', valueColor: '#8097D6' },
  ];

  // ── Page title / subtitle based on mode ──────────────────────────────────
  const pageTitle = isViewEdit
    ? 'Ver / Editar Registro'
    : isFromPlan
    ? `Registrar Pieza${planContext?.numeroPieza ? ` #${planContext.numeroPieza}` : ''}`
    : 'Registro de Piezas';

  const pageSubtitle = isViewEdit
    ? 'Modifica los datos del loncheado existente'
    : isFromPlan
    ? '📋 Datos precargados desde Plan de Trabajo — introduce solo los datos de producción'
    : 'Registra nuevas piezas y loncheados en el sistema';

  if (pageLoading || loadingEdit) {
    return <ERPPage><ERPLoading message="Cargando..." /></ERPPage>;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle} - Las Dos Doncellas</title>
      </Helmet>

      <ERPPage>
        {/* Header */}
        <ERPPageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          actions={
            <>
              {!isFromPlanOrEdit && (
                <ERPButton variant="ghost" icon="BarChart3" onClick={() => setShowAnalytics(!showAnalytics)} loading={btnLoading} disabled={btnLoading}>
                  {showAnalytics ? 'Ocultar' : 'Ver'} Analítica
                </ERPButton>
              )}
              {isFromPlanOrEdit && (
                <ERPButton variant="ghost" icon="ArrowLeft" onClick={() => window.location.href = '/plan-trabajo'} disabled={btnLoading} loading={btnLoading} className="flex-1">
                  Volver al Plan
                </ERPButton>
              )}
              <ERPButton variant="secondary" icon="RotateCcw" onClick={handleReset} className="flex-1" disabled={btnLoading || isFieldLocked} loading={btnLoading}>
                Limpiar
              </ERPButton>
              <ERPButton variant="primary" icon="Save" onClick={handleSubmit} loading={btnLoading} disabled={btnLoading}>
                {isViewEdit ? 'Actualizar' : 'Guardar Registro'}
              </ERPButton>
            </>
          }
        />

        {/* Mode badge */}
        {mode === 'edit' && (
          <div className="mb-3 inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/25 rounded-lg px-3 py-1.5">
            <Icon name="Eye" size={13} className="text-blue-400" />
            <span className="text-xs font-oranienbaum text-blue-400">MODO VER / EDITAR — actualizando loncheado existente</span>
          </div>
        )}
        {mode === 'create' && (
          <div className="mb-3 inline-flex items-center gap-2 bg-[#D6EF4E]/10 border border-[#D6EF4E]/25 rounded-lg px-3 py-1.5">
            <Icon name="Plus" size={13} className="text-[#D6EF4E]" />
            <span className="text-xs font-oranienbaum text-[#D6EF4E]">MODO CREAR — nuevo registro para esta pieza</span>
          </div>
        )}

        {/* Plan de Trabajo info banner */}
        {planContext && (
          <div className="mb-4 bg-[#D6EF4E]/8 border border-[#D6EF4E]/25 rounded-xl px-5 py-3 flex items-start gap-3">
            <div className="w-8 h-8 bg-[#D6EF4E]/15 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="ClipboardList" size={16} className="text-[#D6EF4E]" />
            </div>
            <div className="flex-1">
              <p className="font-bold font-oranienbaum text-[#D6EF4E] text-sm">Datos cargados desde Plan de Trabajo</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-2">
                {planContext.clienteNombre && (
                  <div>
                    <span className="text-[10px] text-[#D6EF4E]/50 font-oranienbaum uppercase">Cliente</span>
                    <p className="text-xs text-[#D6EF4E]/80 font-oranienbaum">{planContext.clienteNombre}</p>
                  </div>
                )}
                {planContext.productoNombre && (
                  <div>
                    <span className="text-[10px] text-[#D6EF4E]/50 font-oranienbaum uppercase">Producto</span>
                    <p className="text-xs text-[#D6EF4E]/80 font-oranienbaum">{planContext.productoNombre}</p>
                  </div>
                )}
                {planContext.empleadoNombre && (
                  <div>
                    <span className="text-[10px] text-[#D6EF4E]/50 font-oranienbaum uppercase">Empleado</span>
                    <p className="text-xs text-[#D6EF4E]/80 font-oranienbaum">👤 {planContext.empleadoNombre}</p>
                  </div>
                )}
              </div>
              {(planContext.formatoCorte || planContext.quiereHuesos || planContext.guardarMerma) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {planContext.formatoCorte && (
                    <span className="text-[10px] bg-[#D6EF4E]/10 text-[#D6EF4E]/80 px-2 py-0.5 rounded border border-[#D6EF4E]/20">
                      Formato: {planContext.formatoCorte}
                    </span>
                  )}
                  {planContext.quiereHuesos && (
                    <span className="text-[10px] bg-amber-400/10 text-amber-400/80 px-2 py-0.5 rounded border border-amber-400/20">🦴 Quiere huesos</span>
                  )}
                  {planContext.guardarMerma && (
                    <span className="text-[10px] bg-green-400/10 text-green-400/80 px-2 py-0.5 rounded border border-green-400/20">♻ Guardar merma</span>
                  )}
                </div>
              )}
              {isFieldLocked && (
                <p className="font-oranienbaum text-xs text-[#D6EF4E]/50 mt-2">
                  Cliente, producto, empleado y condiciones están bloqueados. Solo introduce los datos reales de producción.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Success Banner */}
        {showSuccessMessage && (
          <div className="mb-4 bg-[#3A7F6B]/15 border border-[#3A7F6B]/40 rounded-xl px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-[#3A7F6B]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="CheckCircle2" size={20} className="text-[#3A7F6B]" />
            </div>
            <div>
              <p className="font-bold font-oranienbaum text-[#3A7F6B] text-base">
                {isViewEdit ? 'Registro actualizado correctamente' : 'Pieza registrada correctamente'}
              </p>
              <p className="font-oranienbaum text-sm text-[#3A7F6B]/70">
                {isFromPlanOrEdit ? 'Volviendo al Plan de Trabajo...' : 'El registro se ha guardado y el formulario ha sido limpiado.'}
              </p>
            </div>
          </div>
        )}

        {/* KPIs */}
        <ERPKPIGrid cols={4}>
          {kpiCards.map((k) => (
            <ERPKPICard key={k.title} {...k} loading={kpisLoading} />
          ))}
        </ERPKPIGrid>

        {/* Error */}
        {submitError && (
          <div className="mb-4">
            <ERPError message={submitError} onRetry={() => setSubmitError(null)} />
          </div>
        )}

        {/* Analytics Panel */}
        {showAnalytics && !isFromPlanOrEdit && (
          <div className="mb-5">
            <ERPCard title="Analítica de Rendimiento" titleAction={null}>
              <RendimientoAnalytics />
            </ERPCard>
          </div>
        )}

        {/* Plan de Trabajo Selector — only shown in standalone mode */}
        {mode === 'standalone' && (
          <PlanTrabajoSelector
            onContextLoaded={handleSelectorContextLoaded}
            onClear={handleSelectorClear}
            currentPiezaId={planPiezaId || null}
          />
        )}

        {/* Main Form — Two Columns */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Client & Employee */}
              <ERPCard title={isFieldLocked ? "Asignación — PLANIFICADO" : "Asignación"} titleAction={null}>
                <div className="space-y-4">
                  <div>
                    <label className="block font-oranienbaum mb-1.5 text-white text-[19px]">
                      Cliente *
                      {isFieldLocked && <span className="ml-2 text-[10px] text-[#D6EF4E]/60 bg-[#D6EF4E]/10 px-1.5 py-0.5 rounded">PLANIFICADO</span>}
                    </label>
                    <ClientSelector
                      value={formData?.clientId}
                      onChange={(id) => { if (!isFieldLocked) setFormData((p) => ({ ...p, clientId: id })); }}
                      error={errors?.clientId}
                      disabled={isFieldLocked}
                    />
                    {errors?.clientId && <p className="text-[11px] text-[#E05252] mt-1 font-oranienbaum">{errors.clientId}</p>}
                  </div>
                  <div>
                    <label className="block font-oranienbaum mb-1.5 text-white text-[19px]">
                      Empleado *
                      {isFieldLocked && <span className="ml-2 text-[10px] text-[#D6EF4E]/60 bg-[#D6EF4E]/10 px-1.5 py-0.5 rounded">PLANIFICADO</span>}
                    </label>
                    <EmployeeSelector
                      value={formData?.employeeId}
                      onChange={(id) => { if (!isFieldLocked) setFormData((p) => ({ ...p, employeeId: id })); }}
                      error={errors?.employeeId}
                      disabled={isFieldLocked}
                    />
                    {errors?.employeeId && <p className="text-[11px] text-[#E05252] mt-1 font-oranienbaum">{errors.employeeId}</p>}
                  </div>
                </div>
              </ERPCard>

              {/* Piece Details */}
              <ERPCard title={isFieldLocked ? "Detalles de la Pieza — PLANIFICADO" : "Detalles de la Pieza"} titleAction={null}>
                <PieceDetailsForm
                  formData={formData}
                  onChange={(key, val) => {
                    if (isFieldLocked && (key === 'cutType' || key === 'productId')) return;
                    setFormData((p) => ({ ...p, [key]: val }));
                  }}
                  errors={errors}
                  lockedFields={isFieldLocked ? ['cutType', 'productId'] : []}
                />
              </ERPCard>

              {/* Observaciones */}
              <ERPCard title="Observaciones" titleAction={null}>
                {planContext?.observacionesPlan && (
                  <div className="mb-3 p-2 rounded-lg bg-[#D6EF4E]/5 border border-[#D6EF4E]/15 text-[11px] text-[#D6EF4E]/70 font-oranienbaum italic">
                    📋 Instrucciones del plan: {planContext.observacionesPlan}
                  </div>
                )}
                <textarea
                  value={formData?.observaciones}
                  onChange={(e) => setFormData((p) => ({ ...p, observaciones: e.target.value }))}
                  placeholder="Notas adicionales sobre la pieza..."
                  rows={4}
                  className="w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors resize-none"
                />
              </ERPCard>

              {/* Lotes y Trazabilidad */}
              <ERPCard title="Lotes y Trazabilidad" titleAction={null}>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Lote Interno</label>
                    <input
                      type="text"
                      value={formData?.loteInterno}
                      onChange={e => setFormData(p => ({ ...p, loteInterno: e.target.value }))}
                      placeholder="LDD-3 / 690"
                      className={inputCls}
                    />
                    <p className="text-[10px] font-oranienbaum text-white/30 mt-1">
                      Valor por defecto: LDD-3 / 690. Puede modificarse manualmente.
                    </p>
                  </div>

                  <div>
                    <label className={labelCls}>
                      Lote Externo
                      {generatingLote && (
                        <span className="ml-2 text-[#D6EF4E]/60 text-xs">
                          <Icon name="Loader2" size={10} className="inline animate-spin" /> Generando…
                        </span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData?.loteExterno}
                        onChange={e => setFormData(p => ({ ...p, loteExterno: e.target.value }))}
                        placeholder="LDD-YYYYMMDD-001"
                        className={`${inputCls} flex-1`}
                      />
                      <button
                        type="button"
                        onClick={() => generateLoteExterno(formData?.registrationDate || new Date().toISOString().split('T')[0])}
                        disabled={generatingLote}
                        title="Regenerar lote externo"
                        className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors disabled:opacity-40"
                      >
                        <Icon name="RefreshCw" size={14} />
                      </button>
                    </div>
                    <p className="text-[10px] font-oranienbaum text-white/30 mt-1">
                      Identificador principal de trazabilidad. Formato: LDD-YYYYMMDD-XXX
                    </p>
                  </div>

                  {isLasDosCliente && (
                    <div>
                      <label className={labelCls}>Nº de Blísteres</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData?.numeroBlisteres}
                        onChange={e => setFormData(p => ({ ...p, numeroBlisteres: e.target.value }))}
                        placeholder="0"
                        className={inputCls}
                      />
                      <p className="text-[10px] font-oranienbaum text-white/30 mt-1">
                        Número de blísteres obtenidos de esta pieza.
                      </p>
                    </div>
                  )}
                </div>
              </ERPCard>

              {/* Tarifa Preview */}
              <ERPCard title="Vista Previa — Coste de Mano de Obra" titleAction={null}>
                {tarifaPreview !== null ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#D6EF4E]/8 border border-[#D6EF4E]/20 rounded-xl p-4 text-center">
                        <p className="text-xs font-oranienbaum text-white/50 mb-1">Coste Cortador</p>
                        <p className="text-2xl font-bold font-oranienbaum text-[#D6EF4E]">{tarifaPreview} €</p>
                        <p className="text-[10px] font-oranienbaum text-white/40 mt-1">Salario trabajador</p>
                      </div>
                      <div className="bg-[#C9A24D]/8 border border-[#C9A24D]/20 rounded-xl p-4 text-center">
                        <p className="text-xs font-oranienbaum text-white/50 mb-1">Coste Sala</p>
                        <p className="text-2xl font-bold font-oranienbaum text-[#C9A24D]">{costeSalaPreview} €</p>
                        <p className="text-[10px] font-oranienbaum text-white/40 mt-1">Coste empresa</p>
                      </div>
                    </div>
                    <div className="bg-white/3 border border-white/8 rounded-lg px-3 py-2.5 flex items-center gap-2">
                      <Icon name="Info" size={13} style={{ color: '#D6EF4E', flexShrink: 0 }} />
                      <p className="text-xs font-oranienbaum text-white/70">{tarifaDescripcion}</p>
                    </div>
                    <div className="bg-blue-500/8 border border-blue-500/20 rounded-lg px-3 py-2 flex items-start gap-2">
                      <Icon name="AlertCircle" size={12} style={{ color: '#60a5fa', flexShrink: 0, marginTop: 1 }} />
                      <p className="text-[10px] font-oranienbaum text-blue-300">
                        Este es el <strong>coste de mano de obra</strong> del cortador. La facturación al cliente se determina por la casuística/tipo de servicio configurado, no por el peso.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center mb-3">
                      <Icon name="Calculator" size={20} style={{ color: 'rgba(255,255,255,0.25)' }} />
                    </div>
                    <p className="text-sm font-oranienbaum text-white/35">Introduce el peso y tipo de gestión</p>
                    <p className="text-xs font-oranienbaum text-white/20 mt-1">para ver el coste de mano de obra</p>
                  </div>
                )}
              </ERPCard>

              {/* Fotografía */}
              <ERPCard title="Fotografía" titleAction={null}>
                <FotografiaSection
                  fotos={formData?.fotos}
                  onChange={(fotos) => setFormData((p) => ({ ...p, fotos }))}
                  loncheadoId={savedLoncheadoId}
                />
              </ERPCard>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Rendimiento */}
              <ERPCard title="Rendimiento y Merma" titleAction={null}>
                <RendimientoSection
                  formData={formData}
                  onChange={(key, val) => setFormData((p) => ({ ...p, [key]: val }))}
                  errors={errors}
                />
              </ERPCard>

              {/* Real-time Summary */}
              <ERPCard title="Resumen en Tiempo Real" titleAction={null}>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#111111] rounded-lg p-3 border border-white/5">
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Peso Inicial</p>
                      <p className="text-lg font-bold font-oranienbaum text-white/85">
                        {formData?.weight ? `${parseFloat(formData.weight).toFixed(2)} kg` : '—'}
                      </p>
                    </div>
                    <div className="bg-[#111111] rounded-lg p-3 border border-white/5">
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Peso Aprovechado</p>
                      <p className="text-lg font-bold font-oranienbaum text-white/85">
                        {formData?.pesoAprovechado ? `${parseFloat(formData.pesoAprovechado).toFixed(2)} kg` : '—'}
                      </p>
                    </div>
                    <div className="bg-[#E05252]/8 rounded-lg p-3 border border-[#E05252]/15">
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Merma</p>
                      <p className="text-lg font-bold font-oranienbaum text-[#E05252]">
                        {mermaRealTime != null ? `${parseFloat(mermaRealTime).toFixed(2)} kg` : '—'}
                      </p>
                    </div>
                    <div className={`rounded-lg p-3 border ${rendimientoRealTime >= 70 ? 'bg-[#3A7F6B]/8 border-[#3A7F6B]/15' : rendimientoRealTime > 0 ? 'bg-[#C9A24D]/8 border-[#C9A24D]/15' : 'bg-white/3 border-white/5'}`}>
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Rendimiento</p>
                      <p className={`text-lg font-bold font-oranienbaum ${rendimientoRealTime >= 70 ? 'text-[#3A7F6B]' : rendimientoRealTime > 0 ? 'text-[#C9A24D]' : 'text-white/85'}`}>
                        {rendimientoRealTime != null && rendimientoRealTime > 0 ? `${rendimientoRealTime.toFixed(1)}%` : '—'}
                      </p>
                    </div>
                  </div>

                  {rendimientoRealTime > 0 && (
                    <div className="mt-2">
                      <div className="flex justify-between text-[10px] font-oranienbaum text-white/40 mb-1">
                        <span>Rendimiento</span>
                        <span>{rendimientoRealTime.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${rendimientoRealTime >= 70 ? 'bg-[#3A7F6B]' : rendimientoRealTime >= 50 ? 'bg-[#C9A24D]' : 'bg-[#E05252]'}`}
                          style={{ width: `${Math.min(rendimientoRealTime, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ERPCard>

              {/* Save Button */}
              <div className="flex gap-3">
                {!isFromPlanOrEdit && (
                  <ERPButton variant="secondary" icon="RotateCcw" onClick={handleReset} className="flex-1" disabled={btnLoading} loading={btnLoading}>
                    Limpiar Formulario
                  </ERPButton>
                )}
                {isFromPlanOrEdit && (
                  <ERPButton variant="ghost" icon="ArrowLeft" onClick={() => window.location.href = '/plan-trabajo'} disabled={btnLoading} loading={btnLoading} className="flex-1">
                    Volver al Plan
                  </ERPButton>
                )}
                <ERPButton variant="primary" icon="Save" onClick={handleSubmit} loading={btnLoading} disabled={btnLoading} className="flex-1">
                  {isViewEdit ? 'Actualizar Registro' : 'Guardar Registro'}
                </ERPButton>
              </div>
            </div>
          </div>
        </form>
      </ERPPage>
    </>
  );
};

export default PieceRegistration;